// The word diff of a correction, shared by interpals/pm/correction.js and react/forums.
// It mirrors Interpals\Helpers\TextDiff (word tokens with whitespace kept, LCS, a replacement above the cell cap). Keep the two in sync.

const MAX_LCS_CELLS = 250000;

// Whitespace and ideograph ranges shared verbatim with TextDiff (PHP). \s and \p{Script=...} differ between V8 and PCRE.
// Ideographic and Thai scripts tokenize per code point. Space-delimited scripts (Hangul too) tokenize per word.
const WS = '\\t\\n\\x0B\\f\\r \\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000\\uFEFF';
const IDEO = '\\u0E00-\\u0E7F\\u3001-\\u303F\\u3040-\\u30FF\\u31F0-\\u31FF'
    + '\\u3400-\\u4DBF\\u4E00-\\u9FFF\\uF900-\\uFAFF\\uFF66-\\uFF9F\\u{20000}-\\u{2FA1F}';
const TOKEN_RX = new RegExp('[' + WS + ']+|[' + IDEO + ']|[^' + WS + IDEO + ']+', 'gu');

// The segment classes of pm/partials/message-body.twig, so every correction marks its edits the same way.
export const DIFF_DELETE_CLASSES = 'line-through decoration-rose-400/70 decoration-[1.5px] text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 rounded-[3px] px-0.5';
export const DIFF_INSERT_CLASSES = 'no-underline font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/40 rounded-[3px] px-0.5';

function tokenize(text) {
    if (!text) return [];
    return text.match(TOKEN_RX) || [];
}

function replacementDiff(oldStr, newStr) {
    const segments = [];
    if (oldStr !== '') segments.push({ text: oldStr, op: 'del' });
    if (newStr !== '') segments.push({ text: newStr, op: 'ins' });
    return segments;
}

/**
 * Word-level diff between two strings.
 * Returns ordered segments {text, op} with op one of 'eq' | 'del' | 'ins'.
 */
export function textDiff(oldStr, newStr) {
    oldStr = String(oldStr == null ? '' : oldStr);
    newStr = String(newStr == null ? '' : newStr);

    if (oldStr === newStr) {
        return oldStr === '' ? [] : [{ text: oldStr, op: 'eq' }];
    }

    const oldTokens = tokenize(oldStr);
    const newTokens = tokenize(newStr);
    const n = oldTokens.length;
    const m = newTokens.length;

    if (n === 0 || m === 0 || n * m > MAX_LCS_CELLS) {
        return replacementDiff(oldStr, newStr);
    }

    // LCS length table, flattened row-major into a typed array.
    const width = m + 1;
    const lcs = new Int32Array((n + 1) * width);
    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            if (oldTokens[i - 1] === newTokens[j - 1]) {
                lcs[i * width + j] = lcs[(i - 1) * width + (j - 1)] + 1;
            } else {
                const up = lcs[(i - 1) * width + j];
                const left = lcs[i * width + (j - 1)];
                lcs[i * width + j] = up > left ? up : left;
            }
        }
    }

    // Backtrack in reverse. Preferring insert on ties puts deletions before insertions within each replaced run.
    const reversed = [];
    let bi = n;
    let bj = m;
    while (bi > 0 || bj > 0) {
        if (bi > 0 && bj > 0 && oldTokens[bi - 1] === newTokens[bj - 1]) {
            reversed.push({ op: 'eq', text: oldTokens[bi - 1] });
            bi--;
            bj--;
        } else if (bj > 0 && (bi === 0 || lcs[bi * width + (bj - 1)] >= lcs[(bi - 1) * width + bj])) {
            reversed.push({ op: 'ins', text: newTokens[bj - 1] });
            bj--;
        } else {
            reversed.push({ op: 'del', text: oldTokens[bi - 1] });
            bi--;
        }
    }

    const segments = [];
    for (let k = reversed.length - 1; k >= 0; k--) {
        const part = reversed[k];
        const last = segments[segments.length - 1];
        if (last && last.op === part.op) {
            last.text += part.text;
        } else {
            segments.push({ text: part.text, op: part.op });
        }
    }
    return segments;
}
